import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiCreditCard } from "react-icons/fi";
import {
  DIGITAP_POLL_MAX_MS,
  DIGITAP_POLL_MS,
  DIGITAP_POLL_NETWORK_FAIL_MAX,
  DIGITAP_POST_CLOSE_POLL_MS,
  DIGITAP_SEND_KYC_URL,
  DIGITAP_WINDOW_WATCH_MS,
  digitapKycDetailsUrl,
  getDigitapSendKycUrl,
} from "../../config/digitapKyc";
import { sanitizeAadhaarProfile } from "../../utils/applyFlowStorage";
import {
  clearApplyLoanDigitapDraft,
  clearDigilockerReturnPath,
  getApplyLoanDigitapCallbackTxn,
  getApplyLoanDigitapDraft,
  isApplyLoanDigitapCallbackDone,
  markApplyLoanDigitapCallbackDone,
  persistApplyLoanAadhaarNumber,
  saveApplyLoanDigitapCallbackTxn,
  saveApplyLoanDigitapDraft,
  saveApplyLoanStep,
  saveDigilockerReturnPath,
} from "../../utils/applyLoanStorage";
import {
  clearApplyLoanDigitapReturnParams,
  DIGITAP_CALLBACK_MESSAGE_TYPE,
  getDigitapKycUrl,
  getDigitapTransactionId,
  parseDigitapKycDetailsStatus,
  readApplyLoanDigitapReturnParams,
} from "../../utils/digitapKycUtils";
import { checkCustomerByPan } from "../../utils/panCustomerCheck";
import "./AadhaarInput.css";

const PAN_STATUS_VERIFIED = "PAN Verification Complete!";
const AADHAAR_STATUS_VERIFIED = "Aadhaar Verification Complete!";

const isPanNotFoundOnline = (responseCode, responseMessage) =>
  responseCode === "ENI004" ||
  String(responseMessage || "").toLowerCase().includes("no information found");

const IconField = ({ icon: Icon, children, className = "" }) => (
  <div className={`loan-icon-field ${className}`.trim()}>
    <span className="loan-icon-field__icon" aria-hidden="true">
      <Icon />
    </span>
    {children}
  </div>
);

const mergeLoanFormPan = (pan, panDetails, panStatus) => {
  let loan = {};
  try {
    const raw = localStorage.getItem("loanFormData");
    if (raw) loan = JSON.parse(raw);
  } catch {
    loan = {};
  }
  localStorage.setItem(
    "loanFormData",
    JSON.stringify({
      ...loan,
      pan,
      panStatus: panStatus || PAN_STATUS_VERIFIED,
      pan_verified: true,
      ...panDetails,
    })
  );
};

const readStoredAadhaarVerified = () => {
  try {
    const flagged = localStorage.getItem("aadhaar_verified") === "true";
    const raw = localStorage.getItem("aadhaarData");
    if (!raw) return { verified: false };
    const profile = JSON.parse(raw);
    if (!profile || typeof profile !== "object") return { verified: false };
    const hasProfile = Boolean(profile.name || profile.fullName);
    return { verified: flagged || hasProfile };
  } catch {
    return { verified: false };
  }
};

/**
 * DigiLocker Aadhaar.
 * /apply-loan → sendAadhaarOtp2.php
 * /landing    → sendAadhaarOtpla.php (pass enquirySource="landing" or sendKycUrl)
 * Poll: getDigitapKycDetails. sameTab (/apply-now) redirects instead of a popup.
 */
const ApplyLoanDigitapIdentityStep1 = ({
  onNext,
  hideHero = false,
  enquirySource,
  sendKycUrl,
  sameTab = false,
  digilockerReturn = null,
}) => {
  const digitapSendUrl =
    sendKycUrl || getDigitapSendKycUrl(enquirySource) || DIGITAP_SEND_KYC_URL;
  const restoredAadhaar = readStoredAadhaarVerified();
  const initialDraft = getApplyLoanDigitapDraft();

  const [pan, setPan] = useState("");
  const [panVerifying, setPanVerifying] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [panError, setPanError] = useState("");
  const [panStatus, setPanStatus] = useState("");
  const [panDetails, setPanDetails] = useState({});
  const panVerifyLockRef = useRef(false);
  const digilockerStartLockRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [justVerified, setJustVerified] = useState(false);
  const [aadhaarVerified, setAadhaarVerified] = useState(restoredAadhaar.verified);
  const [aadhaarStatus, setAadhaarStatus] = useState(
    restoredAadhaar.verified
      ? AADHAAR_STATUS_VERIFIED
      : panVerified
        ? "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
        : ""
  );
  const [aadhaarError, setAadhaarError] = useState("");
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [digitapPending, setDigitapPending] = useState(
    Boolean(initialDraft?.pending && !restoredAadhaar.verified)
  );
  const [digitapDraft, setDigitapDraft] = useState(initialDraft);

  const pollStartedAtRef = useRef(initialDraft?.startedAt || 0);
  const pollLockRef = useRef(false);
  const pollNetworkFailRef = useRef(0);
  const pollTimerRef = useRef(null);
  const digilockerWinRef = useRef(null);
  const windowWatchRef = useRef(null);
  const closeGraceTimerRef = useRef(null);
  const bootstrappedRef = useRef(false);
  const aadhaarVerifiedRef = useRef(restoredAadhaar.verified);
  const pollDigitapVerificationRef = useRef(null);

  const canContinue = panVerified && !aadhaarVerified && !digitapPending && !loading;

  useEffect(() => {
    aadhaarVerifiedRef.current = aadhaarVerified;
  }, [aadhaarVerified]);

  const stopPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const stopWindowWatch = useCallback(() => {
    if (windowWatchRef.current) {
      clearInterval(windowWatchRef.current);
      windowWatchRef.current = null;
    }
  }, []);

  const stopCloseGrace = useCallback(() => {
    if (closeGraceTimerRef.current) {
      clearTimeout(closeGraceTimerRef.current);
      closeGraceTimerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    stopPollTimer();
    stopWindowWatch();
    stopCloseGrace();
  }, [stopCloseGrace, stopPollTimer, stopWindowWatch]);

  const unlockContinueWithoutVerify = useCallback(() => {
    stopPolling();
    setDigitapPending(false);
    setLoading(false);
    setAadhaarError(
      "DigiLocker tab closed without verification. Click Continue to open DigiLocker again."
    );
    setAadhaarStatus(
      panVerified
        ? "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
        : ""
    );
  }, [panVerified, stopPolling]);

  /** DigiLocker redirect: keep polling getDigitapKycDetails — advance only on Success + name/DOB */
  const resumeDigitapAfterCallback = useCallback((transactionId) => {
    const txnId = String(transactionId || "").replace(/\D/g, "");
    if (!txnId || aadhaarVerifiedRef.current) return;

    saveApplyLoanDigitapCallbackTxn(txnId);
    const existing = getApplyLoanDigitapDraft();
    saveApplyLoanDigitapDraft({
      pan: existing?.pan || "",
      transactionId: txnId,
      kycUrl: existing?.kycUrl || "",
      startedAt: existing?.startedAt || Date.now(),
      mobileVerified: true,
      panVerified: true,
    });
    setDigitapPending(true);
    setAadhaarError("");
    setAadhaarStatus(
      "DigiLocker complete. Waiting for KYC details (name/DOB)..."
    );
    startDigitapPollingRef.current?.(txnId, existing?.startedAt || Date.now());
  }, []);

  const startDigitapPollingRef = useRef(null);
  const resumeDigitapAfterCallbackRef = useRef(null);

  const watchDigilockerWindow = useCallback(
    (win) => {
      stopWindowWatch();
      if (!win) return;
      digilockerWinRef.current = win;
      windowWatchRef.current = setInterval(() => {
        try {
          // DigiLocker callback: verifyAadhaarOtp2 (apply-loan) / verifyAadhaarOtpla (landing)
          // → /apply-loan or /landing?txnId=&aadhaarVerified=1
          try {
            const href = String(win.location?.href || "");
            if (
              href &&
              (href.includes("aadhaarVerified=1") ||
                href.includes("aadhaar_verified=1"))
            ) {
              const u = new URL(href);
              const txn = String(
                u.searchParams.get("txnId") ||
                  u.searchParams.get("txn_id") ||
                  u.searchParams.get("transactionId") ||
                  ""
              ).replace(/\D/g, "");
              stopWindowWatch();
              try {
                win.close();
              } catch {
                /* ignore */
              }
              digilockerWinRef.current = null;
              if (txn) {
                resumeDigitapAfterCallbackRef.current?.(txn);
              }
              return;
            }
          } catch {
            /* still on DigiLocker cross-origin — ignore */
          }

          if (!win.closed) return;
          stopWindowWatch();
          digilockerWinRef.current = null;
          if (aadhaarVerifiedRef.current) return;

          setAadhaarStatus(
            "DigiLocker closed. Checking verification status — keep this page open..."
          );
          setAadhaarError("");
          setDigitapPending(true);

          const draft = getApplyLoanDigitapDraft();
          const txn =
            draft?.transactionId || getApplyLoanDigitapCallbackTxn() || "";
          if (txn) {
            // Poll only AFTER DigiLocker closes — not while user is still on DigiLocker
            const graceStartedAt =
              Date.now() - (DIGITAP_POLL_MAX_MS - DIGITAP_POST_CLOSE_POLL_MS);
            startDigitapPollingRef.current?.(txn, graceStartedAt);
          }

          stopCloseGrace();
          closeGraceTimerRef.current = setTimeout(() => {
            if (!aadhaarVerifiedRef.current) {
              unlockContinueWithoutVerify();
            }
          }, DIGITAP_POST_CLOSE_POLL_MS);
        } catch {
          /* ignore */
        }
      }, DIGITAP_WINDOW_WATCH_MS);
    },
    [stopCloseGrace, stopWindowWatch, unlockContinueWithoutVerify]
  );

  const finalizeDigitapProfile = useCallback(
    (digitapProfile) => {
      if (!digitapProfile) return;

      const lastFour = String(digitapProfile.aadhaarLastFour || "").replace(/\D/g, "").slice(-4);
      const placeholderAadhaar = lastFour
        ? `xxxxxxxx${lastFour}`.replace(/x/g, "0").slice(-12)
        : "";

      const resolvedAddress =
        digitapProfile.full_address ||
        digitapProfile.fullAddress ||
        digitapProfile.address ||
        "";

      const safeProfile = sanitizeAadhaarProfile({
        name: digitapProfile.fullName || digitapProfile.name || "",
        fullName: digitapProfile.fullName || digitapProfile.name || "",
        firstName: digitapProfile.firstName || "",
        middleName: digitapProfile.middleName || "",
        lastName: digitapProfile.lastName || "",
        dateOfBirth: digitapProfile.dob || "",
        dob: digitapProfile.dob || "",
        gender: digitapProfile.gender || "",
        address: resolvedAddress,
        full_address: resolvedAddress,
        fullAddress: resolvedAddress,
        aadhaar_address: resolvedAddress,
        district: digitapProfile.district || "",
        dist: digitapProfile.district || "",
        state: digitapProfile.state || "",
        pincode: digitapProfile.pincode || "",
        pc: digitapProfile.pincode || "",
        mobile: digitapProfile.mobile || "",
        email: digitapProfile.email || "",
        aadhaar_number: placeholderAadhaar,
        aadhaarNumber: placeholderAadhaar,
        maskedAdharNumber: digitapProfile.maskedAdharNumber || "",
        aadhaarLastFour: lastFour,
      });

      if (placeholderAadhaar.length === 12) {
        persistApplyLoanAadhaarNumber(placeholderAadhaar);
      }
      localStorage.setItem("aadhaarData", JSON.stringify(safeProfile));
      localStorage.setItem("aadhaar_verified", "true");
      clearApplyLoanDigitapDraft();
      clearApplyLoanDigitapReturnParams();
      setDigitapDraft(null);
      setDigitapPending(false);
      stopPolling();
      try {
        digilockerWinRef.current?.close();
      } catch {
        /* ignore */
      }

      mergeLoanFormPan(pan, panDetails, panStatus || PAN_STATUS_VERIFIED);
      try {
        const loan = JSON.parse(localStorage.getItem("loanFormData") || "{}");
        const fullName =
          safeProfile.name ||
          [digitapProfile.firstName, digitapProfile.middleName, digitapProfile.lastName]
            .filter(Boolean)
            .join(" ");
        localStorage.setItem(
          "loanFormData",
          JSON.stringify({
            ...loan,
            aadhaar_number: placeholderAadhaar || loan.aadhaar_number || "",
            aadhaar: placeholderAadhaar || loan.aadhaar || "",
            aadhaarLastFour: lastFour,
            firstName: digitapProfile.firstName || loan.firstName,
            lastName: digitapProfile.lastName || loan.lastName,
            first_name: digitapProfile.firstName || loan.first_name,
            middle_name: digitapProfile.middleName || loan.middle_name,
            last_name: digitapProfile.lastName || loan.last_name,
            fullName,
            dob: digitapProfile.dob || loan.dob,
            gender: digitapProfile.gender || loan.gender,
            address: resolvedAddress || loan.address,
            full_address: resolvedAddress || loan.full_address || "",
            aadhaar_address: resolvedAddress || loan.aadhaar_address || "",
            district: digitapProfile.district || loan.district,
            state: digitapProfile.state || loan.state,
            pincode: digitapProfile.pincode || loan.pincode,
            city: digitapProfile.district || loan.city,
            city_name: digitapProfile.district || loan.city_name,
            email: digitapProfile.email || loan.email || loan.email_personal || "",
            email_personal:
              digitapProfile.email || loan.email_personal || loan.email || "",
            digitap_verified: true,
          })
        );
      } catch {
        /* ignore */
      }

      localStorage.setItem("pan_verified", "true");
      saveApplyLoanStep(3);

      setJustVerified(true);
      setAadhaarVerified(true);
      setAadhaarStatus(AADHAAR_STATUS_VERIFIED);
      setAadhaarError("");
      setTimeout(() => onNext?.(), 500);
    },
    [onNext, pan, panDetails, panStatus, stopPolling]
  );

  useEffect(() => {
    resumeDigitapAfterCallbackRef.current = resumeDigitapAfterCallback;
  }, [resumeDigitapAfterCallback]);

  // DigiLocker popup redirects to /landing or /apply-loan — handoff via postMessage
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== DIGITAP_CALLBACK_MESSAGE_TYPE) return;
      const txn = String(data.txnId || "").replace(/\D/g, "");
      if (!txn || aadhaarVerifiedRef.current) return;
      try {
        window.focus();
      } catch {
        /* ignore */
      }
      resumeDigitapAfterCallbackRef.current?.(txn);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const pollDigitapVerification = useCallback(
    async (transactionId) => {
      const txnId = String(transactionId || "").replace(/\D/g, "");
      if (!txnId || pollLockRef.current) return false;

      pollLockRef.current = true;
      try {
        const url = digitapKycDetailsUrl(txnId);
        let data = null;
        try {
          const res = await axios.get(url, {
            timeout: 30000,
            validateStatus: () => true,
            withCredentials: false,
          });
          if (res.data && typeof res.data === "object") {
            data = res.data;
          }
        } catch {
          pollNetworkFailRef.current += 1;
          if (pollNetworkFailRef.current >= DIGITAP_POLL_NETWORK_FAIL_MAX) {
            stopPolling();
            setDigitapPending(false);
            setAadhaarError(
              "Unable to check DigiLocker status (network/CORS). Please try Continue again, or use https://fundswalle.com (not www)."
            );
            setAadhaarStatus("");
            return false;
          }
          setAadhaarStatus(
            "Waiting for DigiLocker KYC details... Keep this page open."
          );
          return false;
        }

        if (!data) {
          pollNetworkFailRef.current += 1;
          if (pollNetworkFailRef.current >= DIGITAP_POLL_NETWORK_FAIL_MAX) {
            stopPolling();
            setDigitapPending(false);
            setAadhaarError(
              "Unable to check DigiLocker status (network/CORS). Please try Continue again, or use https://fundswalle.com (not www)."
            );
            setAadhaarStatus("");
            return false;
          }
          setAadhaarStatus(
            "Waiting for DigiLocker KYC details... Keep this page open."
          );
          return false;
        }

        // Got JSON — reset fail streak (pending KYC is OK, keep polling)
        pollNetworkFailRef.current = 0;

        const parsed = parseDigitapKycDetailsStatus(data);

        if (parsed.state === "success") {
          markApplyLoanDigitapCallbackDone(txnId);
          stopCloseGrace();
          finalizeDigitapProfile(parsed.profile);
          return true;
        }

        if (parsed.state === "failed") {
          stopPolling();
          setDigitapPending(false);
          setAadhaarError(parsed.message);
          setAadhaarStatus("");
          return false;
        }

        setAadhaarStatus(
          parsed.message ||
            "Complete Aadhaar verification in the DigiLocker window. Keep this page open."
        );
        return false;
      } catch {
        setAadhaarStatus(
          "Complete Aadhaar verification in the DigiLocker window. Keep this page open."
        );
        return false;
      } finally {
        pollLockRef.current = false;
      }
    },
    [finalizeDigitapProfile, stopCloseGrace, stopPolling]
  );

  useEffect(() => {
    pollDigitapVerificationRef.current = pollDigitapVerification;
  }, [pollDigitapVerification]);

  const startDigitapPolling = useCallback(
    (transactionId, startedAt) => {
      // Only stop poll timer — do NOT kill DigiLocker window watch
      stopPollTimer();
      const txnId = String(transactionId || "").replace(/\D/g, "");
      if (!txnId) return;

      pollStartedAtRef.current = startedAt || Date.now();
      pollNetworkFailRef.current = 0;
      setDigitapPending(true);

      const tick = async () => {
        if (aadhaarVerifiedRef.current) {
          stopPollTimer();
          return;
        }
        if (Date.now() - pollStartedAtRef.current > DIGITAP_POLL_MAX_MS) {
          stopPollTimer();
          setDigitapPending(false);
          setAadhaarError(
            "Verification timed out. Click Continue to open DigiLocker again."
          );
          return;
        }
        // Skip if previous request still in flight (avoids stacked XRHs)
        if (pollLockRef.current) return;
        await pollDigitapVerificationRef.current?.(txnId);
      };

      tick();
      pollTimerRef.current = setInterval(tick, DIGITAP_POLL_MS);
    },
    [stopPollTimer]
  );

  useEffect(() => {
    startDigitapPollingRef.current = startDigitapPolling;
  }, [startDigitapPolling]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("loanFormData");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const savedPan = String(parsed.pan || parsed.pan_verifiedPan || "")
        .toUpperCase()
        .trim();
      if (
        savedPan.length === 10 &&
        (parsed.pan_verified === true ||
          parsed.pan_verified === "true" ||
          localStorage.getItem("pan_verified") === "true")
      ) {
        setPan(savedPan);
        setPanVerified(true);
        setPanStatus(parsed.panStatus || PAN_STATUS_VERIFIED);
        setPanDetails({
          pan_responseCode: parsed.pan_responseCode || "",
          pan_responseMessage: parsed.pan_responseMessage || "",
          pan_referenceId: parsed.pan_referenceId || "",
          pan_firstName: parsed.pan_firstName || "",
          pan_lastName: parsed.pan_lastName || "",
          pan_address: parsed.pan_address || "",
          pan_gender: parsed.pan_gender || "",
          pan_aadhaarLinked: parsed.pan_aadhaarLinked || "",
          pan_dob: parsed.pan_dob || "",
          pan_fullName: parsed.pan_fullName || "",
          pan_category: parsed.pan_category || "",
          pan_maskedAadhaarNumber: parsed.pan_maskedAadhaarNumber || "",
          pan_verifiedPan: parsed.pan_verifiedPan || savedPan,
        });
        if (!restoredAadhaar.verified) {
          setAadhaarStatus(
            "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
          );
        }
      }
    } catch {
      /* ignore */
    }
  }, [restoredAadhaar.verified]);

  useEffect(() => {
    if (bootstrappedRef.current || aadhaarVerifiedRef.current) return undefined;
    bootstrappedRef.current = true;

    // Read from window.location.search (Router search can be empty briefly).
    // Parent may already have scrubbed the URL — fall back to what it captured.
    const urlReturnParams = readApplyLoanDigitapReturnParams();
    const returnParams = urlReturnParams.txnId
      ? urlReturnParams
      : digilockerReturn?.txnId
        ? digilockerReturn
        : urlReturnParams;
    if (returnParams.txnId) {
      saveApplyLoanDigitapCallbackTxn(returnParams.txnId);
      const existing = getApplyLoanDigitapDraft();
      saveApplyLoanDigitapDraft({
        pan: existing?.pan || "",
        transactionId: returnParams.txnId,
        kycUrl: existing?.kycUrl || "",
        startedAt: existing?.startedAt || Date.now(),
        mobileVerified: true,
        panVerified: true,
      });
      clearApplyLoanDigitapReturnParams();
    }

    const draft = getApplyLoanDigitapDraft();
    const callbackTxn = getApplyLoanDigitapCallbackTxn();
    const txnId = returnParams.txnId || callbackTxn || draft?.transactionId || "";

    if (draft) {
      setDigitapDraft(draft);
      if (draft.pan && draft.pan.length === 10) {
        setPan((prev) => prev || draft.pan);
        setPanVerified(true);
        setPanStatus(PAN_STATUS_VERIFIED);
      }
    }

    // Resume poll ONLY after DigiLocker callback landed back on this page.
    // Never auto-hammer getDigitapKycDetails just because an old txn draft exists.
    // Same-tab return: user spent time on DigiLocker, so poll window starts now
    const startedAt =
      sameTab && returnParams.txnId ? Date.now() : draft?.startedAt || Date.now();
    const ageMs = Date.now() - Number(startedAt || 0);
    const stillFresh = ageMs >= 0 && ageMs < DIGITAP_POLL_MAX_MS;
    const shouldResumePoll =
      Boolean(txnId) &&
      stillFresh &&
      (Boolean(returnParams.aadhaarVerified) || Boolean(returnParams.txnId)) &&
      !isApplyLoanDigitapCallbackDone(txnId);

    if (shouldResumePoll) {
      setDigitapPending(true);
      setAadhaarStatus(
        "DigiLocker complete. Waiting for KYC details (name/DOB)..."
      );
      startDigitapPollingRef.current?.(txnId, startedAt);
    } else if (txnId && !stillFresh) {
      clearApplyLoanDigitapDraft();
      setDigitapPending(false);
    } else if (draft?.pending && draft?.kycUrl && stillFresh) {
      // Mid DigiLocker session after refresh — wait for user to Continue again; no poll spam
      setDigitapPending(true);
      setAadhaarStatus(
        "Complete Aadhaar verification in DigiLocker. Click Continue if the window closed."
      );
    }

    const onVisible = () => {
      if (document.visibilityState !== "visible" || aadhaarVerifiedRef.current) {
        return;
      }
      // Only one-shot check while an active poll session is running
      if (!pollTimerRef.current) return;
      const pending = getApplyLoanDigitapDraft();
      const txn =
        pending?.transactionId || getApplyLoanDigitapCallbackTxn() || txnId;
      if (txn) {
        pollDigitapVerificationRef.current?.(txn);
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    const onStorage = (e) => {
      if (aadhaarVerifiedRef.current) return;
      if (e.key === "aadhaar_verified" && e.newValue === "true") {
        try {
          const raw = localStorage.getItem("aadhaarData");
          const profile = raw ? JSON.parse(raw) : null;
          if (profile && (profile.name || profile.fullName)) {
            setJustVerified(true);
            setAadhaarVerified(true);
            setDigitapPending(false);
            setAadhaarStatus(AADHAAR_STATUS_VERIFIED);
            stopPolling();
            setTimeout(() => onNext?.(), 300);
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
      stopPolling();
    };
    // Mount-once — polling uses refs so unstable parent callbacks don't re-fire intervals
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePanVerify = async (val) => {
    if (!val || val.length !== 10) return;
    if (panVerifyLockRef.current) return;

    panVerifyLockRef.current = true;
    setPanVerifying(true);
    setPanError("");
    setPanStatus("");

    const verifyPanUrl = "https://fundswalla.in/loan_backend/verifyPan.php";

    try {
      const customerCheck = await checkCustomerByPan(val);
      if (customerCheck.blocked) {
        setPanDetails({});
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        setPanStatus("");
        setPanError(
          customerCheck.message ||
            "This PAN is already registered. Cannot re-apply."
        );
        return;
      }

      const statusMessage =
        customerCheck.isFresh || !customerCheck.message
          ? PAN_STATUS_VERIFIED
          : customerCheck.message;

      const verifyRes = await axios.get(verifyPanUrl, {
        params: { pan: val },
        timeout: 30000,
      });
      const verifyData = verifyRes.data?.data || {};
      const panInfo = verifyData?.data || {};
      const responseCode = verifyData.responseCode || "";
      const responseMessage = verifyData.responseMessage || "";
      const aadhaarLinked =
        panInfo.aadhaarLinked === true ||
        panInfo.aadhaarLinked === "true" ||
        panInfo.aadhaarLinked === "Y"
          ? "true"
          : panInfo.aadhaarLinked === false ||
              panInfo.aadhaarLinked === "false" ||
              panInfo.aadhaarLinked === "N"
            ? "false"
            : "";

      const nextPanDetails = {
        pan_responseCode: responseCode,
        pan_responseMessage: responseMessage,
        pan_referenceId: verifyData.referenceId || "",
        pan_firstName: panInfo.firstName || "",
        pan_lastName: panInfo.lastName || "",
        pan_address: panInfo.address || "",
        pan_gender: panInfo.gender || "",
        pan_aadhaarLinked: aadhaarLinked,
        pan_dob: panInfo.dob || "",
        pan_fullName: panInfo.fullName || "",
        pan_category: panInfo.category || "",
        pan_maskedAadhaarNumber: panInfo.maskedAadhaarNumber || "",
        pan_verifiedPan: panInfo.pan || val,
      };

      if (responseCode === "SRC001") {
        setPanDetails(nextPanDetails);
        setPanStatus(statusMessage);
        setPanVerified(true);
        localStorage.setItem("pan_verified", "true");
        mergeLoanFormPan(val, nextPanDetails, statusMessage);
        setAadhaarStatus(
          "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
        );
        setAadhaarError("");
        return;
      }

      if (isPanNotFoundOnline(responseCode, responseMessage)) {
        setPanDetails({});
        setPanStatus("");
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        setPanError("PAN does not exist. Enter your correct PAN.");
        return;
      }

      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError("Please enter a valid PAN number.");
    } catch {
      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError("PAN verification failed. Please try again.");
      setPanStatus("");
    } finally {
      panVerifyLockRef.current = false;
      setPanVerifying(false);
    }
  };

  const resetDigitap = () => {
    stopPolling();
    digilockerStartLockRef.current = false;
    clearApplyLoanDigitapDraft();
    clearApplyLoanDigitapReturnParams();
    setDigitapDraft(null);
    setDigitapPending(false);
    setPopupBlocked(false);
    setAadhaarError("");
    setAadhaarVerified(false);
    setAadhaarStatus(
      panVerified
        ? "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
        : ""
    );
    localStorage.setItem("aadhaar_verified", "false");
    localStorage.removeItem("aadhaarData");
  };

  const startDigitapKyc = async () => {
    if (!panVerified || loading || digitapPending || digilockerStartLockRef.current) {
      return;
    }
    digilockerStartLockRef.current = true;

    setPopupBlocked(false);
    setAadhaarError("");
    setAadhaarStatus("Waiting for DigiLocker...");
    setLoading(true);

    if (sameTab) {
      try {
        const res = await axios.post(
          digitapSendUrl,
          {},
          {
            headers: { "Content-Type": "application/json" },
            timeout: 30000,
            validateStatus: () => true,
          }
        );
        const data = res.data || {};
        const kycUrl = getDigitapKycUrl(data);
        const transactionId = getDigitapTransactionId(data);

        if (!kycUrl || !transactionId) {
          setAadhaarError(
            "Unable to start DigiLocker. KYC URL / transactionId missing. Please try Continue again."
          );
          setAadhaarStatus(
            "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
          );
          setLoading(false);
          digilockerStartLockRef.current = false;
          return;
        }

        const draft = {
          pan,
          transactionId,
          kycUrl,
          startedAt: Date.now(),
          mobileVerified: true,
          panVerified: true,
        };
        saveApplyLoanDigitapDraft(draft);
        setDigitapDraft(draft);
        setDigitapPending(true);
        setAadhaarStatus("Redirecting to DigiLocker...");
        saveDigilockerReturnPath(window.location.pathname);
        // Navigating away — keep lock so a second click cannot start another KYC
        window.location.assign(kycUrl);
      } catch {
        setAadhaarError(
          "Unable to start DigiLocker verification. Please try again."
        );
        setAadhaarStatus(
          "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
        );
        setLoading(false);
        digilockerStartLockRef.current = false;
      }
      return;
    }

    clearDigilockerReturnPath();
    const preOpenedWindow = window.open("about:blank", "_blank");
    if (!preOpenedWindow) {
      setPopupBlocked(true);
      setAadhaarError(
        "Popup blocked. Allow popups for this site, then click Continue again."
      );
      setAadhaarStatus("");
      setLoading(false);
      digilockerStartLockRef.current = false;
      return;
    }

    try {
      const res = await axios.post(
        digitapSendUrl,
        {},
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
          validateStatus: () => true,
        }
      );

      const data = res.data || {};
      // Ignore top-level status:false — use nested digitap_response.model
      const kycUrl = getDigitapKycUrl(data);
      const transactionId = getDigitapTransactionId(data);

      if (!kycUrl || !transactionId) {
        preOpenedWindow.close();
        setAadhaarError(
          "Unable to start DigiLocker. KYC URL / transactionId missing. Please try Continue again."
        );
        setAadhaarStatus(
          "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
        );
        return;
      }

      const startedAt = Date.now();
      const draft = {
        pan,
        transactionId,
        kycUrl,
        startedAt,
        mobileVerified: true,
        panVerified: true,
      };
      // Never clear mid-flow — only update draft
      saveApplyLoanDigitapDraft(draft);
      setDigitapDraft(draft);
      setDigitapPending(true);
      setAadhaarStatus(
        "Complete Aadhaar verification in the DigiLocker window. Keep this page open."
      );

      try {
        preOpenedWindow.location.replace(kycUrl);
      } catch {
        preOpenedWindow.location.href = kycUrl;
      }
      watchDigilockerWindow(preOpenedWindow);
      // Do NOT poll getDigitapKycDetails in a loop while DigiLocker is open.
      // Polling starts only after popup closes / callback returns.
    } catch {
      preOpenedWindow.close();
      setAadhaarError(
        "Unable to start DigiLocker verification. Please try again."
      );
      setAadhaarStatus(
        "PAN verified. Continue to complete Aadhaar verification in DigiLocker."
      );
    } finally {
      setLoading(false);
      digilockerStartLockRef.current = false;
    }
  };

  if (justVerified) {
    return (
      <div className="apply-form-head" style={{ textAlign: "center", border: "none" }}>
        <h2>{"\u2705"} Verified!</h2>
        <p>Redirecting...</p>
      </div>
    );
  }

  return (
    <div className="apply-now1-focus apply-loan-mobile-focus">
      <div className="apply-now1-focus-card">
        {!hideHero && (
          <div className="apply-form-head">
            <div className="apply-now1-hero">
              <span className="apply-now1-hero__eyebrow">Start Application</span>
              <h2>Verify identity</h2>
              <p>Verify PAN, then continue to DigiLocker</p>
            </div>
          </div>
        )}

        <div className="apply-field">
          <label>
            PAN Number <span className="req">*</span>
          </label>
          <IconField icon={FiCreditCard}>
            <input
              type="text"
              maxLength={10}
              value={pan}
              placeholder="ABCDE1234F"
              disabled={digitapPending || panVerifying || aadhaarVerified}
              onChange={(e) => {
                const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                setPan(v);
                setPanError("");
                setPanStatus("");
                setPanVerified(false);
                setAadhaarStatus("");
                localStorage.setItem("pan_verified", "false");
                if (v.length === 10) handlePanVerify(v);
              }}
              className={`apply-flow-input ${panVerified ? "valid" : ""} ${
                panError ? "error" : ""
              }`}
            />
          </IconField>
          {panVerifying && (
            <div className="apply-flow-success">Verifying PAN...</div>
          )}
          {panStatus && !panError && (
            <div className="apply-flow-success">{panStatus}</div>
          )}
          {panError && <div className="apply-flow-error">{panError}</div>}
        </div>

        {aadhaarStatus && !aadhaarError && (
          <div className="apply-flow-success">{aadhaarStatus}</div>
        )}
        {aadhaarError && <div className="apply-flow-error">{aadhaarError}</div>}
        {popupBlocked && (
          <div className="apply-flow-error">
            Popup blocked. Allow popups for this site, then click Continue again.
          </div>
        )}

        {aadhaarVerified ? (
          <div className="apply-flow-actions apply-flow-actions--inline">
            <button
              type="button"
              className="apply-flow-btn apply-flow-btn--secondary"
              onClick={resetDigitap}
            >
              Re-verify Aadhaar
            </button>
            <button
              type="button"
              className="apply-flow-btn apply-flow-btn--primary"
              onClick={() => onNext?.()}
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="apply-flow-actions apply-flow-actions--inline">
            {digitapPending && (
              <button
                type="button"
                className="apply-flow-btn apply-flow-btn--secondary"
                onClick={resetDigitap}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              className="apply-flow-btn apply-flow-btn--primary"
              disabled={!canContinue && !digitapPending}
              onClick={
                digitapPending
                  ? () => {
                      if (digitapDraft?.kycUrl && sameTab) {
                        saveDigilockerReturnPath(window.location.pathname);
                        window.location.assign(digitapDraft.kycUrl);
                      } else if (digitapDraft?.kycUrl) {
                        const win = window.open(digitapDraft.kycUrl, "_blank");
                        if (!win) {
                          setPopupBlocked(true);
                          return;
                        }
                        setPopupBlocked(false);
                        watchDigilockerWindow(win);
                        // Poll starts after DigiLocker closes — not while open
                      } else {
                        setDigitapPending(false);
                        startDigitapKyc();
                      }
                    }
                  : startDigitapKyc
              }
            >
              {digitapPending || loading
                ? "Waiting for DigiLocker..."
                : "Continue"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplyLoanDigitapIdentityStep1;
